/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react'
import Highcharts from 'highcharts2'
import styled, { useTheme } from 'styled-components'
import i18n from 'live/services/i18n'
import { RawWellEvent, InterventionSeries, RawInterventionEvent, TimeSpan, InterventionEvent } from '../../utils/type'

import { getInterventionChartOptions } from './chart-options'
import { StaticDataModal } from '../modal-components'
import { ChartContainer, ChartTitle } from '../chart-components-style'
import {
    STATIC_DATA_STATES,
    renderButtons,
    StaticDataType,
    ButtonOptions,
    STATES_COLORS,
    renderCreationTooltip,
    DeleteConfirmationModal
} from '../chart-components'
import InterventionModal from './InterventionModal'
import { StaticDataContext } from '../../context/StaticDataContext'
import { INTERVENTION_EMPTY_DATA } from '../../utils/mock'
import { deleteSingleEntity, INTERVENTION_API } from '../../api/api'
import moment from 'moment'
import { deleteWellNoIntervention } from '../../api/wellApi'

// eslint-disable-next-line @typescript-eslint/no-var-requires
require('highcharts/modules/xrange')(Highcharts)

const mapRawInterventionEventsToSeriesData = (
    events: RawInterventionEvent[],
    chartTimeSpan: TimeSpan,
    rigId: number
): InterventionEvent[] => {
    const emptyIntervals: InterventionEvent[] = []
    if (!events?.length) return emptyIntervals

    if (events.length > 1) {
        events.forEach((event, i) => {
            if (event?.end && events[i + 1]?.start && event.end < events[i + 1].start) {
                emptyIntervals.push({
                    name: 'empty',
                    color: STATES_COLORS[STATIC_DATA_STATES.EMPTY],
                    x: event.end,
                    x2: events[i + 1].start,
                    y: 0,
                    rawEvent: { start: event.end, state: STATIC_DATA_STATES.EMPTY, rig_id: rigId }
                })
            }
        })
    }

    return [
        ...emptyIntervals,
        ...events.map((event: RawInterventionEvent, index) =>
            getInterventionEvent(event, events[index + 1], chartTimeSpan)
        )
    ]
}

const getInterventionEvent = (
    intervention: RawInterventionEvent,
    nextIntervention: RawInterventionEvent | undefined,
    chartTimeSpan: TimeSpan
) => ({
    name: intervention.name,
    color: intervention.end
        ? STATES_COLORS[STATIC_DATA_STATES.FINISHED]
        : intervention.id
        ? STATES_COLORS[STATIC_DATA_STATES.STARTED_IN_EXECUTION]
        : STATES_COLORS[STATIC_DATA_STATES.EMPTY],
    x: moment(intervention?.start).valueOf(),
    x2: intervention?.end || nextIntervention?.start || chartTimeSpan?.end,
    y: 0,
    isRealtime: !!intervention?.isRealtime,
    rawEvent: {
        ...intervention,
        state: !intervention?.end ? STATIC_DATA_STATES.STARTED_IN_EXECUTION : STATIC_DATA_STATES.FINISHED
    }
})

export const getInterventionSeries = (
    data: Array<RawInterventionEvent>,
    chartTimeSpan: TimeSpan,
    rigId: number
): InterventionSeries => {
    return {
        name: StaticDataType.INTERVENTION,
        type: 'xrange',
        data:
            data && data.length > 0
                ? [
                      ...INTERVENTION_EMPTY_DATA(chartTimeSpan.begin, data[0].start, rigId),
                      ...mapRawInterventionEventsToSeriesData(data, chartTimeSpan, rigId),
                      ...(data[data.length - 1].end
                          ? INTERVENTION_EMPTY_DATA(data[data.length - 1].end, chartTimeSpan.end, rigId)
                          : [])
                  ]
                : INTERVENTION_EMPTY_DATA(chartTimeSpan.begin, chartTimeSpan.end, rigId)
    }
}

export const InterventionChart = (): ReactElement => {
    // Static Data Context
    const {
        buttonsRef,
        buttonController,
        setButtonController,
        currentChart,
        setAddEventButtonVisible,
        chartType,
        tooltipPosition,
        setTooltiPosition,
        widgetData,
        updateWidgetData,
        widgetTimeSpan,
        refresh,
        reloadData,
        checkBlockedTrackBeforeAction,
        tryOpenModal
    } = useContext(StaticDataContext)
    const updateSeries = useCallback(
        (newSeries: InterventionSeries) => updateWidgetData('interventionSeries', newSeries),
        []
    )
    // Chart States
    const [highchartsElement, setHighchartsElement] = useState<Highcharts.Chart | null>(null)
    const [chartLoaded, setChartLoaded] = useState<boolean>(false)
    const [chartHeight, setChartHeight] = useState<number>(0)
    const chartRef = useRef<HTMLDivElement>(null)
    const theme = useTheme()

    // Modal States
    const [currentInterventionModalEvent, setCurrentInterventionModalEvent] = useState<RawInterventionEvent>(null)
    const [currentWellModalEvent, setCurrentWellModalEvent] = useState<RawWellEvent>(null)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const closeModal = (): void => setIsModalOpen(false)
    const openModal = (rawInterventionEvent: RawInterventionEvent, rawWellEvent: RawWellEvent): void => {
        setCurrentInterventionModalEvent(rawInterventionEvent)
        setCurrentWellModalEvent(rawWellEvent)
        setInterventionTooltipMessage(null)
        setIsModalOpen(true)
    }

    // Delete Confirmation Modal State
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState<boolean>(false)
    const closeDeleteConfirmationModal = (): void => setIsDeleteConfirmationModalOpen(false)
    const openDeleteConfirmationModal = (rawInterventionEvent: RawInterventionEvent): void => {
        setCurrentInterventionModalEvent(rawInterventionEvent)
        setIsDeleteConfirmationModalOpen(true)
    }

    const deleteIntervention = (interventionId: any, wellId: number) => {
        return deleteSingleEntity(INTERVENTION_API, interventionId).then(() => {
            deleteWellNoIntervention(wellId)
            closeDeleteConfirmationModal()
            reloadData()
        })
    }

    function draw(): void {
        try {
            highchartsElement?.destroy()
            setChartLoaded(false)
            setHighchartsElement(
                Highcharts.chart(
                    chartRef?.current,
                    getInterventionChartOptions(
                        { timeSpan: widgetTimeSpan, height: chartHeight },
                        widgetData.interventionSeries,
                        theme,
                        openModal,
                        setAddEventButtonVisible,
                        setTooltiPosition,
                        setInterventionTooltipMessage,
                        tryOpenModal
                    ),
                    () => setChartLoaded(true)
                )
            )
        } catch (err) {
            // highchartsElement does not exits.
        }
    }

    useEffect(() => setChartHeight(chartRef.current.getBoundingClientRect().height), [])

    useEffect(() => draw(), [widgetData.interventionSeries])

    useEffect(() => {
        if (chartLoaded) {
            setTimeout(() => {
                draw()
            }, 300)
        }
    }, [refresh, chartHeight, theme])
    const [interventionTooltipMessage, setInterventionTooltipMessage] = useState(null)

    // const formatDate = moment.unix(series.data[0].x / 1000)
    // const formatTime = moment.unix(series.data[0].x / 1000)

    // TODO TOOL TIP MESSAGE
    // const interventionTooltipMessage = {
    //     timespam: `${formatDate.format('L')} . ${formatTime.format('LTS')}`,
    //     text: `${i18n('Click to add')} ${i18n('Intervention')}`
    // }
    // const interventionTooltipMessage = { timespam: '', text: 'click' }

    const buttonProps = {
        buttonFunctions: [
            () =>
                checkBlockedTrackBeforeAction(currentChart, () => {
                    openDeleteConfirmationModal(currentChart?.rawEvent as RawInterventionEvent)
                    setButtonController(false)
                }),

            () =>
                checkBlockedTrackBeforeAction(currentChart, () => {
                    openModal(
                        currentChart?.rawEvent as RawInterventionEvent,
                        widgetData.wells.find(w => w.id == currentChart?.rawEvent?.well_id) as RawWellEvent
                    )
                    setButtonController(false)
                })
        ],
        customIcons: [ButtonOptions.Delete, ButtonOptions.Confirm]
    }

    return (
        <>
            <ChartContainer>
                <StaticDataModal
                    isOpen={isModalOpen}
                    isDouble={false}
                    closeModal={closeModal}
                    content={
                        <InterventionModal
                            event={currentInterventionModalEvent}
                            wellEvent={currentWellModalEvent}
                            closeModal={() => {
                                closeModal()
                            }}
                        />
                    }
                />
                <ChartTitle>
                    <span>{i18n(StaticDataType.INTERVENTION).toUpperCase()}</span>
                </ChartTitle>
                {<Chart ref={chartRef} />}
                {buttonController && buttonController === STATIC_DATA_STATES.EMPTY_DISABLED ? (
                    <div></div>
                ) : buttonController && buttonController !== STATIC_DATA_STATES.EMPTY ? (
                    chartType === 'intervention' && renderButtons(buttonsRef, currentChart, buttonProps)
                ) : (
                    chartType === 'intervention' &&
                    renderCreationTooltip(
                        buttonsRef,
                        currentChart,
                        tooltipPosition,
                        buttonController,
                        interventionTooltipMessage
                    )
                )}
            </ChartContainer>
            <DeleteConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                closeModal={closeDeleteConfirmationModal}
                confirmDeletion={() => {
                    const currentRawEvent = currentChart?.rawEvent as RawInterventionEvent
                    const interventionId = currentRawEvent?.id
                    const wellId = currentRawEvent?.well_id

                    deleteIntervention(interventionId, wellId).then(() => {
                        setButtonController(false)
                    })
                }}
            />
        </>
    )
}

const Chart = styled.div`
    height: 100%;
    width: 100%;
    max-height: 30px;
    min-height: 20px;
    margin-top: -16px;

    .highcharts-container {
        max-height: 30px;
        min-height: 20px;
        .highcharts-root {
            max-height: 30px;
            min-height: 20px;
            .highcharts-series-group {
                max-height: 30px;
                min-height: 20px;
                .highcharts-series highcharts-series-0 highcharts-xrange-series highcharts-tracker {
                    max-height: 30px;
                    min-height: 20px;
                    .highcharts-point {
                        max-height: 30px;
                        min-height: 20px;
                        .highcharts-point highcharts-partfill-original {
                            max-height: 30px;
                            min-height: 20px;
                        }
                    }
                }
            }
        }
    }
`
